import React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import RoomOutlinedIcon from '@mui/icons-material/RoomOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { useAIProfileSummaryQuery } from '../../features/aiAnalytics';
import { PinChip } from '../MessageProfile';
import { skipToken } from '@reduxjs/toolkit/query';
import type { StoryData } from './RandomPatientStory';

interface OnboardingProfilePreviewProps {
  story: StoryData | null;
}

export const OnboardingProfilePreview: React.FC<OnboardingProfilePreviewProps> = ({ story }) => {
  const { data: aiSummary, isLoading: isAiLoading } = useAIProfileSummaryQuery(story?.id ?? skipToken);

  const glassySx = {
    borderRadius: 3,
    backgroundColor: 'rgba(244, 240, 255, 0.55)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: 'none',
    boxShadow: '0 2px 12px rgba(80, 60, 160, 0.06), inset 0 0 0 1px rgba(164, 142, 233, 0.18)',
    p: 2.5,
    maxHeight: 400,
    overflowY: 'auto',
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(101, 110, 255, 0.3)',
      borderRadius: 2,
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(101, 110, 255, 0.3) transparent',
  };

  if (!story) {
    return (
      <Box sx={glassySx}>
        <Stack gap={1.5}>
          <Skeleton animation="wave" variant="rounded" width="100%" height={56} />
          <Skeleton animation="wave" variant="rounded" width="100%" height={60} />
          <Skeleton animation="wave" variant="rounded" width="100%" height={100} />
        </Stack>
      </Box>
    );
  }

  const aiItems = aiSummary?.ai_summary ?? [];

  return (
    <Box sx={glassySx}>
      <Stack gap={2}>
        {/* Profile Header */}
        <Box sx={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: 'none',
          boxShadow: '0 2px 8px rgba(101, 110, 255, 0.08), inset 0 0 0 1px rgba(164, 142, 233, 0.22)',
        }}>
          <Stack direction="row" alignItems="center" gap={1.5} sx={{
            background: 'linear-gradient(135deg, rgba(139,92,246,0.85) 0%, rgba(165,120,255,0.80) 100%)',
            px: 2, py: 1.25,
          }}>
            <Avatar sx={{
              width: 36, height: 36,
              bgcolor: 'rgba(255,255,255,0.2)',
              fontSize: 15, fontWeight: 700, color: 'white',
              border: '2px solid rgba(255,255,255,0.3)',
              flexShrink: 0,
            }}>
              {story.name_public?.[0]?.toUpperCase() ?? '?'}
            </Avatar>
            <Stack direction="column" gap={0.15} sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.9rem', lineHeight: 1.3 }}>
                {story.name_public}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center" sx={{ px: 2, py: 1, backgroundColor: 'rgba(247, 246, 255, 0.8)', flexWrap: 'wrap' }}>
            <PinChip category="User" type={story.type} size="small" iconSizePx={14} sx={{ height: '22px', fontSize: '0.65rem' }} />
            {story.formattedsearchterm && (
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                <RoomOutlinedIcon sx={{ fontSize: 14, color: '#656EFF', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ color: '#555', lineHeight: 1.3, fontSize: 11 }}>{story.formattedsearchterm}</Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Story Section */}
        <Box sx={{ p: 1.5, borderRadius: '10px', backgroundColor: 'rgba(247, 246, 255, 0.6)', border: '1px solid rgba(238, 237, 255, 0.8)' }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Box sx={{
              minWidth: 28, height: 28, borderRadius: '8px',
              background: 'linear-gradient(135deg, #EEEDFF 0%, #D4D2FF 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AutoStoriesIcon sx={{ fontSize: 14, color: '#656EFF' }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: 13 }}>
              Story
            </Typography>
          </Stack>
          <Box sx={{ position: 'relative', pl: 1.5, py: 0.5, backgroundColor: 'rgba(255, 255, 255, 0.5)', borderRadius: 1 }}>
            <FormatQuoteIcon sx={{ position: 'absolute', top: -3, left: 2, fontSize: 14, color: 'rgba(101, 110, 255, 0.25)', transform: 'scaleX(-1)' }} />
            <Typography variant="body2" sx={{
              color: '#555', lineHeight: 1.5, fontSize: 12,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {story.comment}
            </Typography>
          </Box>
        </Box>

        {/* Health Profile Section */}
        <Box sx={{ p: 1.5, borderRadius: '10px', backgroundColor: 'rgba(247, 246, 255, 0.6)', border: '1px solid rgba(238, 237, 255, 0.8)' }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
            <Box sx={{
              minWidth: 28, height: 28, borderRadius: '8px',
              background: 'linear-gradient(135deg, #EEEDFF 0%, #D4D2FF 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <LocalHospitalIcon sx={{ fontSize: 14, color: '#656EFF' }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: 13 }}>
              Health Profile
            </Typography>
          </Stack>
          {isAiLoading ? (
            <Stack gap={1}>
              <Skeleton animation="wave" variant="rounded" width="100%" height={36} />
              <Skeleton animation="wave" variant="rounded" width="100%" height={36} />
            </Stack>
          ) : aiItems.length > 0 ? (
            <Stack gap={0.75}>
              {aiItems.slice(0, 4).map((row: any, i: number) => {
                const similarCount = row.analytics
                  ? (row.type === 'numeric' ? row.analytics?.users?.length : Array.isArray(row.analytics) ? row.analytics.length : 0)
                  : 0;
                return (
                  <Box key={row.var + i} sx={{ border: '1px solid rgba(238,238,238,0.8)', borderRadius: 1.5, p: 1, backgroundColor: 'rgba(255,255,255,0.5)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="overline" sx={{ color: '#9E9E9E', fontSize: 9, letterSpacing: 1, lineHeight: 1.4, display: 'block' }}>
                          {row.formattedVariable}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1a1a2e', fontWeight: 500, fontSize: 12, lineHeight: 1.4 }}>
                          {row.formattedValue}
                        </Typography>
                      </Box>
                      {similarCount > 0 && (
                        <Chip
                          icon={<PeopleOutlineIcon sx={{ fontSize: 12, color: '#656EFF !important' }} />}
                          label={`${similarCount} similar`}
                          size="small"
                          sx={{
                            flexShrink: 0, height: 22, fontSize: 10, fontWeight: 600,
                            color: '#656EFF', backgroundColor: 'rgba(101, 110, 255, 0.08)',
                            border: '1px solid rgba(101, 110, 255, 0.15)',
                            '& .MuiChip-icon': { ml: 0.5 },
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ color: '#aaa', fontStyle: 'italic', fontSize: 12, textAlign: 'center', py: 1 }}>
              No health profile details available
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
